import { useCallback, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GalleryPhoto {
  image?: string;
  original_url?: string;
  alt_text?: string;
  title?: string;
}

// ─── Slot Group Definitions ───────────────────────────────────────────────────

const introImageDef: SlotGroupDefinition = {
  label: 'Intro Section Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[1].alt_text',
      defaultValue: 'Property interior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a photo that best captures the overall character of this community — an inviting model interior, a standout living space, or an attractive building exterior.',
    context:
      'Hero image sitting beside the home page intro copy that welcomes visitors to the property; it sets the first visual impression, so favor a polished, high-quality shot.',
    sourceCollection: 'photo_gallery',
  },
};

// ─── Component ─────────────────────────────────────────────────────────────────

export function HomeIntroSection() {
  const introEyebrow = useContentToken('homeintrosection.eyebrow');
  // Read the resolved value purely to drive the "Read more" length heuristic
  // below; the visible body is rendered as richtext via <RichTextContent>.
  const introBody = useContentToken('homeintrosection.body');

  const introImage = useSlotGroup('homeintrosection.image', introImageDef);

  const galleryImages = useCollectionBinding<GalleryPhoto>('homeintrosection.gallery_images', {
    collection: 'photo_gallery',
    defaultLimit: 12,
  });

  // The intro tile binds to `photo_gallery[1].image`, but many gallery photos
  // populate only `original_url`. Resolve against the raw gallery photo so an
  // `original_url`-only photo still renders.
  const resolveTileImage = useCallback(
    (slotUrl: string | null | undefined, index: number) => {
      const fallback = galleryImages[index] ?? null;
      return slotUrl || fallback?.image || fallback?.original_url || '';
    },
    [galleryImages],
  );
  const resolveTileAlt = useCallback(
    (slotAlt: string | null | undefined, index: number, defaultAlt: string) => {
      const fallback = galleryImages[index] ?? null;
      return slotAlt || fallback?.alt_text || defaultAlt;
    },
    [galleryImages],
  );

  const introImageUrl = resolveTileImage(introImage.image, 1);
  const introImageAlt = resolveTileAlt(introImage.alt, 1, 'Property interior');

  // Long descriptions can be lengthy. Show a "Read more" toggle that visually
  // clamps the body. The full text always stays in the DOM (only hidden via CSS
  // line-clamp) so web scrapers / SSG output can still read the whole thing.
  const BODY_CLAMP_THRESHOLD = 280;
  const [bodyExpanded, setBodyExpanded] = useState(false);
  const isBodyLong = (introBody?.length ?? 0) > BODY_CLAMP_THRESHOLD;

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-[52%_48%] gap-10 lg:gap-8 items-center">
          {/* Left: text */}
          <div className="lg:pr-12">
            <EditableToken
              path="homeintrosection.eyebrow"
              defaultValue="Premium living awaits"
              as="p"
              className="font-body text-sm text-[var(--vibe-color-text)] mb-4"
            >
              {introEyebrow}
            </EditableToken>

            <RichTextContent
              path="homeintrosection.heading"
              label="Intro Heading"
              defaultValue='<p>Call <span data-type="field-token" data-field="property_name"></span> Home</p>'
              as="h2"
              className="font-heading text-4xl sm:text-5xl font-normal text-[var(--vibe-color-text)] mb-6 leading-[1.1]"
            />

            <div className="mb-8 max-w-lg">
              <div className="relative">
                <RichTextContent
                  path="homeintrosection.body"
                  label="Intro Body"
                  as="div"
                  className={`overview-intro-body font-body text-base text-[var(--vibe-color-text-muted)] leading-relaxed${
                    isBodyLong && !bodyExpanded ? ' overview-intro-body--clamped' : ''
                  }`}
                />

                {isBodyLong && (
                  <div
                    aria-hidden="true"
                    className={`overview-intro-fade${
                      bodyExpanded ? ' overview-intro-fade--hidden' : ''
                    }`}
                  />
                )}
              </div>

              {isBodyLong && (
                <button
                  type="button"
                  onClick={() => setBodyExpanded((prev) => !prev)}
                  aria-expanded={bodyExpanded}
                  className="overview-intro-readmore font-body text-sm font-bold mt-2 inline-flex items-center"
                >
                  {bodyExpanded ? 'Read less' : 'Read more'}
                </button>
              )}
            </div>
          </div>

          {/* Right: image */}
          <EditableSlotGroup
            groupPath="homeintrosection.image"
            definition={introImageDef}
            className="block lg:pr-8"
          >
            {introImageUrl ? (
              <VibesitesImage
                url={introImageUrl}
                alt={introImageAlt}
                className="w-full rounded-lg object-cover shadow-lg"
                style={{ aspectRatio: '4/3' }}
              />
            ) : (
              <div
                className="w-full rounded-lg bg-[var(--vibe-color-surface)] shadow-lg"
                style={{ aspectRatio: '4/3' }}
              />
            )}
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Visually clamp the body while keeping the full text in the DOM so
           scrapers and SSG output still read all of it. Animate open/close
           via max-height (line-clamp can't be transitioned). Expanded cap is
           generous so long descriptions still fully reveal. */
        .overview-intro-body {
          overflow: hidden;
          max-height: 60rem;
          transition: max-height 0.4s ease;
        }
        .overview-intro-body--clamped {
          /* 6 lines at leading-relaxed (1.625) × text-base (1rem) */
          max-height: 9.75rem;
        }
        /* Gradient fade over the last row of clamped text so it's obvious
           there's more to read, instead of a hard cutoff. */
        .overview-intro-fade {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 3rem;
          pointer-events: none;
          background: linear-gradient(to bottom, rgba(255, 255, 255, 0), #ffffff);
          transition: opacity 0.4s ease;
        }
        .overview-intro-fade--hidden {
          opacity: 0;
        }
        @media (prefers-reduced-motion: reduce) {
          .overview-intro-body,
          .overview-intro-fade {
            transition: none;
          }
        }
        .overview-intro-readmore {
          position: relative;
          z-index: 1;
          color: #000000;
          background-color: #ffffff;
          border: none;
          padding: 0;
          cursor: pointer;
        }
        .overview-intro-readmore:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'HomeIntroSection',
  displayName: 'Home Intro Section',
  tokens: {
    'homeintrosection.eyebrow': {
      label: 'Intro Eyebrow',
      type: 'text',
      defaultValue: 'Premium living awaits',
    },
    'homeintrosection.heading': {
      label: 'Intro Heading',
      type: 'richtext',
      defaultValue:
        '<p>Call <span data-type="field-token" data-field="property_name"></span> Home</p>',
    },
    'homeintrosection.body': {
      label: 'Intro Body',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue:
        '<p>Discover luxury apartments and enjoy premium amenities in the heart of the city. Explore our floor plans and experience upscale urban comfort.</p>',
    },
  },
  slotGroups: {
    'homeintrosection.image': {
      label: 'Intro Section Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'photo_gallery[1].image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultField: 'photo_gallery[1].alt_text',
          defaultValue: 'Property interior',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 1,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
      generator: {
        prompt:
          'Select a photo that best captures the overall character of this community — an inviting model interior, a standout living space, or an attractive building exterior.',
        context:
          'Hero image sitting beside the home page intro copy that welcomes visitors to the property; it sets the first visual impression, so favor a polished, high-quality shot.',
        sourceCollection: 'photo_gallery',
      },
    },
  },
  collectionBindings: {
    'homeintrosection.gallery_images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      defaultLimit: 12,
    },
  },
};

(HomeIntroSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default HomeIntroSection;
